import { ErrorCode } from '../constants/errorCodes';
import { OrderProductStatus } from '../constants/OrderProductConsts';

/**
 * Refresh-rates — when a hotel room in the order is no longer
 * available the backend returns a structured error:
 *
 *   res.data       = { status: 'error', data: {...} }   // API envelope
 *   res.data.data  = { message, code, info }            // inner payload
 *   info.results   = [{ order_id, rooms: [{ id, name, status }] }]
 *
 * Everything below `data` is OPTIONAL — older responses and non-hotel
 * providers may omit `code`/`info`, and `info.results` may be absent. This
 * helper never throws and returns empty results when nothing matches.
 */
export interface UnavailableRoom {
  id?: number;
  name?: string;
  status?: string;
}

export interface RefreshRatesErrorInfo {
  /** true when the inner code is exactly `room_not_available`. */
  isRoomNotAvailable: boolean;
  /** every room whose status is `not_available`, across all provider results. */
  unavailableRooms: UnavailableRoom[];
  /** just the names, de-duplicated and empty-filtered — ready for a toast. */
  roomNames: string[];
  /** the raw inner message, if the backend supplied one. */
  message?: string;
}

/** Parse a refresh-rates (or order-get) axios response for the
 *  "room no longer available" structured error. Safe on any shape. */
export const parseRefreshRatesError = (res: any): RefreshRatesErrorInfo => {
  const inner = res?.data?.data;
  const results: any[] = inner?.info?.results ?? [];

  const unavailableRooms: UnavailableRoom[] = results
    .flatMap((r: any) => r?.rooms ?? [])
    .filter(
      (room: any) => room?.status === OrderProductStatus.NOT_AVAILABLE
    );

  const roomNames = Array.from(
    new Set(
      unavailableRooms
        .map((room: any) => room?.name)
        .filter((name: any): name is string => !!name)
    )
  );

  return {
    isRoomNotAvailable: inner?.code === ErrorCode.ROOM_NOT_AVAILABLE,
    unavailableRooms,
    roomNames,
    message: inner?.message,
  };
};
